import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useRef, } from "react";
import { useQuery } from "react-query";
import { Center, Spinner } from "@chakra-ui/react";
import API from "../shared-utils/api";
/** Removes auth_token from URL for security (single-use, sensitive). */
function removeAuthTokenFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.has("auth_token")) {
        url.searchParams.delete("auth_token");
        return url.pathname + (url.search || "");
    }
    return null;
}
const CustomerContext = createContext(undefined);
export const useCustomer = () => {
    const context = useContext(CustomerContext);
    if (context === undefined) {
        throw new Error("useCustomer must be used within a CustomerProvider");
    }
    return context;
};
// Optional hook that returns null if not within CustomerProvider
export const useCustomerOptional = () => {
    const context = useContext(CustomerContext);
    return context || null;
};
export const CustomerProvider = ({ children, }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem("is_phone_verified") === "true");
    const [isResolvingAuthToken, setIsResolvingAuthToken] = useState(false);
    const hasAttemptedAuthToken = useRef(false);
    // Magic-link: when user opens menu link from WhatsApp with auth_token, exchange for session.
    // Run once on mount; use ref to avoid re-running after login state changes.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const authToken = params.get("auth_token");
        if (!authToken || authToken.trim() === "")
            return;
        if (isLoggedIn) {
            const cleanPath = removeAuthTokenFromUrl();
            if (cleanPath)
                window.history.replaceState({}, "", cleanPath);
            return;
        }
        if (hasAttemptedAuthToken.current)
            return;
        hasAttemptedAuthToken.current = true;
        setIsResolvingAuthToken(true);
        API.get(`/api/v1/rbzo/auth/menu-token-login?token=${encodeURIComponent(authToken)}`)
            .then(() => {
            localStorage.setItem("is_phone_verified", "true");
            setIsLoggedIn(true);
            const cleanPath = removeAuthTokenFromUrl();
            if (cleanPath)
                window.history.replaceState({}, "", cleanPath);
        })
            .catch(() => {
            // Token expired or invalid: redirect to login, then back to menu (previous flow)
            const cleanPath = removeAuthTokenFromUrl();
            const redirectPath = cleanPath || window.location.pathname + window.location.search;
            window.location.href = `/verify-phone/direct?redirect=${encodeURIComponent(redirectPath)}`;
        })
            .finally(() => {
            setIsResolvingAuthToken(false);
        });
    }, []); // Empty deps: run once on mount when auth_token present
    const { data: customerData, error, isLoading, refetch, } = useQuery(["customerProfile"], async () => {
        if (!isLoggedIn)
            return null;
        const { data } = await API.get("/api/v1/rbzo/user/profile");
        return data.data;
    }, {
        enabled: isLoggedIn,
        retry: false,
        staleTime: 5 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
    });
    const login = () => {
        localStorage.setItem("is_phone_verified", "true");
        setIsLoggedIn(true);
    };
    const logout = () => {
        const preservedDob = localStorage.getItem("z_dob_modal_dismissed");
        localStorage.clear();
        if (preservedDob != null) {
            localStorage.setItem("z_dob_modal_dismissed", preservedDob);
        }
        window.location.href = "/home";
    };
    if (isResolvingAuthToken) {
        return (_jsx(Center, { w: "100vw", h: "100vh", background: "#111111", children: _jsx(Spinner, { color: "white" }) }));
    }
    if (isLoading && isLoggedIn) {
        return (_jsx(Center, { w: "100vw", h: "100vh", background: "#111111", children: _jsx(Spinner, { color: "white" }) }));
    }
    return (_jsx(CustomerContext.Provider, { value: {
            customerData: isLoggedIn ? customerData : undefined,
            isLoading,
            error,
            logout,
            refetchProfile: refetch,
            login,
        }, children: children }));
};
